import { useCallback } from 'react';
import type { Control, FieldPath, FieldValues, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { Switch } from './Switch';

export type SwitchFormFieldProps<
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>;
  fieldPath: TFieldPath;
  rules?: UseControllerProps<TFieldValues, TFieldPath>['rules'];
  onBeforeChange?: (checked: boolean) => Promise<void>;
  onChange?: (checked: boolean) => void;
};

export const SwitchFormField = <
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  fieldPath,
  control,
  rules,
  onBeforeChange,
  onChange,
}: SwitchFormFieldProps<TFieldValues, TFieldPath>) => {
  const {
    field: { name, onBlur, onChange: innerOnChange, ref, value },
  } = useController({ name: fieldPath, control, rules });

  const composedOnChange = useCallback(
    (checked: boolean) => {
      if (onBeforeChange) {
        onBeforeChange(checked)
          .then(() => {
            innerOnChange(checked);
            onChange?.(checked);
          })
          .catch(() => {});
      } else {
        innerOnChange(checked);
        onChange?.(checked);
      }
    },
    [onBeforeChange, onChange, innerOnChange],
  );

  return (
    <Switch checked={value} onChange={composedOnChange} ref={ref} name={name} onBlur={onBlur} />
  );
};
